import React, { FC, useState } from 'react'
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Layout } from '@/apps/latest/components/templates/Layout'
import {
  Banner,
  NotionButton,
  NotionTextField,
  PageLoading,
  PageScaffold,
  SubsectionHeader,
} from '@/apps/latest/components/portal/notion'
import { FONT_MONO } from '@/shared/styles/typography'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import {
  useLineSettingsQuery,
  useUpdateLineSettingsMutation,
  useVerifyLineSettingsMutation,
} from '@/features/internal/patient-messaging'
import { useToast } from '@/shared/contexts/ToastContext'
import { RADIUS } from '@/shared/styles/radii'

const LineSettingsPage: FC = () => {
  const { data: settings, isLoading } = useLineSettingsQuery()
  const updateMutation = useUpdateLineSettingsMutation()
  const verifyMutation = useVerifyLineSettingsMutation()
  const { showToast } = useToast()

  const [channelAccessToken, setChannelAccessToken] = useState('')
  const [channelSecret, setChannelSecret] = useState('')
  const [botBasicId, setBotBasicId] = useState('')
  const [copied, setCopied] = useState(false)

  const handleCopyWebhookUrl = async () => {
    if (settings?.webhook_url) {
      await navigator.clipboard.writeText(settings.webhook_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleSave = () => {
    // Basic ID が無いと連携用の QR を作れないので 3 つとも必須 (保存済みの Basic ID は打ち直さなくてよい)
    const basicId = botBasicId || settings?.bot_basic_id || ''
    if (!channelAccessToken || !channelSecret || !basicId) {
      showToast(
        'Channel Access Token・Channel Secret・Bot Basic ID は必須です',
        'error'
      )
      return
    }
    updateMutation.mutate(
      {
        channel_access_token: channelAccessToken,
        channel_secret: channelSecret,
        bot_basic_id: basicId,
      },
      {
        onSuccess: () => {
          showToast('LINE連携設定を保存しました', 'success')
          setChannelAccessToken('')
          setChannelSecret('')
        },
        onError: () => {
          showToast('保存に失敗しました', 'error')
        },
      }
    )
  }

  const handleVerify = () => {
    verifyMutation.mutate(undefined, {
      onSuccess: (data) => {
        showToast(`接続検証に成功しました（Bot: ${data.bot_name}）`, 'success')
      },
      onError: () => {
        showToast(
          'LINE API接続に失敗しました。設定を確認してください。',
          'error'
        )
      },
    })
  }

  const getStatusChip = () => {
    if (!settings) return null
    if (settings.is_verified) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="検証済み"
          color="success"
          size="small"
        />
      )
    }
    if (settings.channel_access_token) {
      return <Chip label="設定済み（未検証）" color="warning" size="small" />
    }
    return <Chip label="未設定" color="default" size="small" />
  }

  if (isLoading) {
    return (
      <Layout breadcrumb={['設定', 'LINE連携設定']}>
        <PageLoading />
      </Layout>
    )
  }

  return (
    <PageScaffold title="LINE連携設定">
      <Stack gap={4}>
        <Box>
          <SubsectionHeader
            heading="接続ステータス"
            actions={getStatusChip()}
          />
          {settings?.is_verified && (
            <Banner severity="success">
              LINE Bot API との接続が確認されています。
            </Banner>
          )}
        </Box>

        <Box>
          <SubsectionHeader
            heading="Webhook URL"
            description="LINE Developers コンソールの Messaging API 設定で、以下の URL を Webhook URL に設定してください。"
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: colorTokens.overlay.soft,
              border: `1px solid ${colorTokens.border.subtle}`,
              borderRadius: `${RADIUS.md}px`,
              px: 1.5,
              py: 1,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontSize: 13,
                fontFamily: FONT_MONO.fontFamily,
                color: 'text.primary',
                wordBreak: 'break-all',
              }}
            >
              {settings?.webhook_url}
            </Typography>
            <Tooltip title={copied ? 'コピーしました' : 'コピー'}>
              <IconButton onClick={handleCopyWebhookUrl} size="small">
                {copied ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box>
          <SubsectionHeader
            heading="チャネル情報"
            description={
              'LINE Developers コンソールから取得したチャネル情報を入力してください。\n既に設定済みの場合、現在の値はマスキングされて表示されます。'
            }
          />
          <Stack gap={2}>
            <NotionTextField
              label="Channel Access Token"
              value={channelAccessToken}
              onChange={(e) => setChannelAccessToken(e.target.value)}
              placeholder={settings?.channel_access_token || '未設定'}
              helperText="Messaging API設定 > チャネルアクセストークン（長期）"
            />
            <NotionTextField
              label="Channel Secret"
              value={channelSecret}
              onChange={(e) => setChannelSecret(e.target.value)}
              placeholder={settings?.channel_secret || '未設定'}
              helperText="チャネル基本設定 > チャネルシークレット"
            />
            <NotionTextField
              label="Bot Basic ID"
              value={botBasicId}
              onChange={(e) => setBotBasicId(e.target.value)}
              placeholder={settings?.bot_basic_id || '未設定（例: @150dsllm）'}
              helperText="Messaging API設定 > ボットのベーシックID"
            />
            <Stack direction="row" gap={1.5} sx={{ mt: 1 }}>
              <NotionButton
                variant="primary"
                onClick={handleSave}
                disabled={
                  updateMutation.isPending ||
                  (!channelAccessToken && !channelSecret)
                }
              >
                {updateMutation.isPending ? '保存中…' : '保存'}
              </NotionButton>
              <NotionButton
                variant="secondary"
                onClick={handleVerify}
                disabled={
                  verifyMutation.isPending || !settings?.channel_access_token
                }
              >
                {verifyMutation.isPending ? '検証中…' : '接続検証'}
              </NotionButton>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </PageScaffold>
  )
}

export default LineSettingsPage
